import { useState, useEffect, useCallback } from 'react';
import {
  getTaskById,
  updateTask,
  updateTaskStatus,
  reassignTask,
  deleteTask,
  restoreTask,
  getTaskHistory
} from '../api/tasks';

/**
 * Custom hook for managing single task details
 * @param {number} taskId - Task ID
 * @param {Object} options - Configuration options
 * @param {boolean} options.autoFetch - Auto fetch on mount (default: true)
 * @param {boolean} options.fetchHistory - Also fetch task history (default: false)
 */
export const useTaskDetail = (taskId, options = {}) => {
  const { autoFetch = true, fetchHistory = false } = options;

  const [task, setTask] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Fetch task by ID
   */
  const fetchTask = useCallback(async () => {
    if (!taskId) return;

    setLoading(true);
    setError(null);

    try {
      const response = await getTaskById(taskId);

      if (response.success) {
        setTask(response.data);

        // If history is included in response
        if (response.data.history) {
          setHistory(response.data.history);
        }
      } else {
        throw new Error(response.message || 'Failed to fetch task');
      }
    } catch (err) {
      console.error('Error fetching task:', err);
      setError(err.response?.data?.message || err.message || 'Failed to fetch task');
      setTask(null);
    } finally {
      setLoading(false);
    }
  }, [taskId]);

  /**
   * Fetch task history
   */
  const fetchTaskHistory = useCallback(async () => {
    if (!taskId) return;

    try {
      const response = await getTaskHistory(taskId);

      if (response.success) {
        setHistory(response.data || []);
      }
    } catch (err) {
      console.error('Error fetching task history:', err);
    }
  }, [taskId]);

  /**
   * Update task
   */
  const update = useCallback(async (updateData) => {
    if (!taskId) return { success: false, message: 'No task ID provided' };

    setUpdating(true);
    setError(null);

    try {
      const response = await updateTask(taskId, updateData);

      if (response.success) {
        setTask(response.data);
        // Refetch history after update
        if (fetchHistory) {
          await fetchTaskHistory();
        }
        return { success: true, data: response.data };
      } else {
        throw new Error(response.message || 'Failed to update task');
      }
    } catch (err) {
      console.error('Error updating task:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Failed to update task';
      setError(errorMessage);
      return { success: false, message: errorMessage };
    } finally {
      setUpdating(false);
    }
  }, [taskId, fetchHistory, fetchTaskHistory]);

  /**
   * Update task status
   */
  const updateStatus = useCallback(async (newStatus, notes = null) => {
    if (!taskId) return { success: false, message: 'No task ID provided' };

    setUpdating(true);
    setError(null);

    try {
      const response = await updateTaskStatus(taskId, newStatus, notes);

      if (response.success) {
        setTask(response.data);
        // Refetch history after status update
        if (fetchHistory) {
          await fetchTaskHistory();
        }
        return { success: true, data: response.data };
      } else {
        throw new Error(response.message || 'Failed to update task status');
      }
    } catch (err) {
      console.error('Error updating task status:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Failed to update task status';
      setError(errorMessage);
      return { success: false, message: errorMessage };
    } finally {
      setUpdating(false);
    }
  }, [taskId, fetchHistory, fetchTaskHistory]);

  /**
   * Reassign task
   */
  const reassign = useCallback(async (newAssignedTo, notes = null) => {
    if (!taskId) return { success: false, message: 'No task ID provided' };

    setUpdating(true);
    setError(null);

    try {
      const response = await reassignTask(taskId, newAssignedTo, notes);

      if (response.success) {
        setTask(response.data);
        // Refetch history after reassignment
        if (fetchHistory) {
          await fetchTaskHistory();
        }
        return { success: true, data: response.data };
      } else {
        throw new Error(response.message || 'Failed to reassign task');
      }
    } catch (err) {
      console.error('Error reassigning task:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Failed to reassign task';
      setError(errorMessage);
      return { success: false, message: errorMessage };
    } finally {
      setUpdating(false);
    }
  }, [taskId, fetchHistory, fetchTaskHistory]);

  /**
   * Delete task
   */
  const remove = useCallback(async () => {
    if (!taskId) return { success: false, message: 'No task ID provided' };

    setUpdating(true);
    setError(null);

    try {
      const response = await deleteTask(taskId);

      if (response.success) {
        return { success: true, message: response.message };
      } else {
        throw new Error(response.message || 'Failed to delete task');
      }
    } catch (err) {
      console.error('Error deleting task:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Failed to delete task';
      setError(errorMessage);
      return { success: false, message: errorMessage };
    } finally {
      setUpdating(false);
    }
  }, [taskId]);

  /**
   * Restore task
   */
  const restore = useCallback(async () => {
    if (!taskId) return { success: false, message: 'No task ID provided' };

    setUpdating(true);
    setError(null);

    try {
      const response = await restoreTask(taskId);

      if (response.success) {
        setTask(response.data);
        return { success: true, data: response.data };
      } else {
        throw new Error(response.message || 'Failed to restore task');
      }
    } catch (err) {
      console.error('Error restoring task:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Failed to restore task';
      setError(errorMessage);
      return { success: false, message: errorMessage };
    } finally {
      setUpdating(false);
    }
  }, [taskId]);

  /**
   * Refetch task
   */
  const refetch = useCallback(() => {
    fetchTask();
    if (fetchHistory) {
      fetchTaskHistory();
    }
  }, [fetchTask, fetchHistory, fetchTaskHistory]);

  // Auto fetch on mount if enabled
  useEffect(() => {
    if (autoFetch && taskId) {
      fetchTask();
      if (fetchHistory) {
        fetchTaskHistory();
      }
    }
  }, [autoFetch, taskId, fetchTask, fetchHistory, fetchTaskHistory]);

  return {
    task,
    history,
    loading,
    updating,
    error,
    fetchTask,
    fetchTaskHistory,
    update,
    updateStatus,
    reassign,
    remove,
    restore,
    refetch
  };
};

export default useTaskDetail;
